import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { IconButton, CircularProgress, Modal, Button } from "@mui/material";
import { styled } from "@mui/material/styles";
import SlidesIcon from "@mui/icons-material/Slideshow";
import { Modal as AntdModal } from "antd";
import SpinLoader from "components/Loader/SpinLoader";
import { getStoredGoogleEmail, storeGoogleEmail, fetchGoogleUserEmail } from "components/auth_flow/useGoogleAuth";
import Stylecomp from "styled-components"
const DRIVE_SCOPE = "https://www.googleapis.com/auth/drive.file";
const LOGIN_TIMEOUT = 60000;

const StyledExportButton = styled(IconButton)(({ theme }) => ({
  color: "white",
  fontSize: 22,
  padding: "4px",
  borderRadius: "20px",
  transition: "all 0.3s ease-in-out",
  "&:hover": {
    backgroundColor: "rgba(255, 255, 255, 0.1)",
  },
  "&:disabled": {
    opacity: 0.6,
  },
}));

const StyledModal = styled(Modal)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 100000002,
}));

const GoogleSlidesButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#fff',
  color: 'black',
  padding: '10px 15px',
  borderRadius: '4px',
  fontWeight: 'bold',
  textTransform: 'none',
  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.25)',
  '&:hover': {
    backgroundColor: '#f5f5f5',
  },
}));
const GoogleClassroomButton = Stylecomp.button`
  background-color: #fff; /* Google Classroom color */
  color: black;
  padding: 10px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  text-align: center;
  display: inline-block;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.25);
  transition: background-color 0.3s;
  font-family: 'Roboto', sans-serif;

  &::before {
    content: '';
    display: inline-block;
    width: 24px;
    height: 24px;
    background-image: url('https://firebasestorage.googleapis.com/v0/b/yo-americano.appspot.com/o/assets%2F96x96_yellow_stroke_icon%402x%20(1).png?alt=media&token=744575cd-08ce-4750-b0c1-117b2f1acdda');
    background-size: cover;
    margin-right: 8px;
    vertical-align: middle;
  }
`;
const ExportToSlides = forwardRef(({ pptxBlob, handleExportDone = () => {}, showAlert = () => {}, generateBlob = null }, ref) => {
  const [isExporting, setIsExporting] = useState(false);
  const [accessToken, setAccessToken] = useState(null);
  const loginActivityRef = useRef(false);
  const popupTimeoutRef = useRef(null);
  const timeoutRef = useRef(null);
  const [currentBlob, setCurrentBlob] = useState(pptxBlob);
  const [item_loading, setItemLoading] = useState(false);
  const [showPopupBlockedModal, setShowPopupBlockedModal] = useState(false);

  const uploadToGoogleDrive = async (blob, token) => {
    const metadata = {
      name: `Presentation_${Date.now()}.pptx`,
      mimeType: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
    };

    const form = new FormData();
    form.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
    form.append("file", blob);

    const res = await fetch(
      "https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart&fields=id",
      {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      }
    );

    const data = await res.json();
    if (!res.ok) throw new Error(data.error?.message || "Upload failed");
    return data.id;
  };

  const resetLoader = () => {
    clearTimeout(timeoutRef.current);
    clearTimeout(popupTimeoutRef.current);
    setIsExporting(false);
    setItemLoading(false);
  };

  useEffect(() => () => resetLoader(), []);
  
  useEffect(() => {
    setCurrentBlob(pptxBlob);
  }, [pptxBlob]);

  function openPopup(fileId) {
    const popup = window.open(
      `https://docs.google.com/presentation/d/${fileId}/edit`,'_blank',);
   
    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      AntdModal.confirm({
        title: "Unable to open Google Slides",
        content: (
          <>
            <p>Your browser blocked this pop-up. Click {" "}
            <a href={`https://docs.google.com/presentation/d/${fileId}/edit`} target="_blank" rel="noopener noreferrer">
              Open Google Slides
            </a>
            {" "}to continue viewing the presentation.</p>
          </>
        ),
        okText: "Close",
        cancelButtonProps: { style: { display: "none" } },
        zIndex: 100000001
      });
      return;
    }
   
    popup.focus();
    return true;
  }

  const handleTokenSuccess = async (tokenResponse) => {
    loginActivityRef.current = true;
    resetLoader();
    try {
      const token = tokenResponse.access_token;
      setItemLoading(true);
      setAccessToken(token);
      
      const userEmail = await fetchGoogleUserEmail(token);
      if (userEmail) {
        storeGoogleEmail(userEmail);
      }
      
      const blobToUse = currentBlob;
      console.log("blobToUse", blobToUse);
      if (blobToUse) {
        const fileId = await uploadToGoogleDrive(blobToUse, token);
        openPopup(fileId);
        handleExportDone();
        setItemLoading(false);
      }
    } catch (err) {
      console.error("Error exporting to Google Slides:", err);
      showAlert(`Export failed: ${err.message}`, "error");
    } finally {
      resetLoader();
    }
  };

  const handleTokenError = (error) => {
    loginActivityRef.current = true;
    console.error("Google login error:", error);
    resetLoader();
    if (error?.error === "popup_closed_by_user" || error?.type === 'popup_closed') {
      showAlert("Login cancelled.", "info");
    } else if (error?.type === 'popup_blocked' || error?.error === 'popup_blocked' || error?.type === 'popup_failed_to_open') {
      setShowPopupBlockedModal(true);
      setItemLoading(false);
    } else {
      showAlert("Google authentication failed. Please try again.", "error");
    }
  };

  const loginRef = useRef(null);
  const interactiveLoginRef = useRef(null);
  const silentLoginFailedRef = useRef(false);
  const storedEmail = getStoredGoogleEmail();

  const handleSilentLoginError = (error) => {
    const needsInteraction =
      error?.error === "interaction_required" ||
      error?.error_subtype === "access_denied";
    setItemLoading(false);
    if (needsInteraction) {
      silentLoginFailedRef.current = true;
      loginActivityRef.current = false;
      if (typeof interactiveLoginRef.current === "function") {
        setTimeout(() => {
          interactiveLoginRef.current();
        }, 100);
      }
    } else {
      handleTokenError(error);
    }
  };

  const handleSilentNonOAuthError = (error) => {
    if (error?.type === 'popup_blocked' || error?.error === 'popup_blocked' || error?.type === 'popup_failed_to_open') {
      setShowPopupBlockedModal(true);
      setItemLoading(false);
    } else if (error?.type === 'popup_closed' || error?.error === 'popup_closed_by_user') {
      showAlert("Login cancelled.", "info");
      setItemLoading(false);
    } else {
      showAlert("Google authentication failed. Please try again.", "error");
      setItemLoading(false);
    }
  };

  const handleInteractiveLoginError = (error) => {
    setItemLoading(false);
    handleTokenError(error);
  };

  const handleInteractiveNonOAuthError = (error) => {
    if (error?.type === 'popup_blocked' || error?.error === 'popup_blocked' || error?.type === 'popup_failed_to_open') {
      setShowPopupBlockedModal(true);
      setItemLoading(false);
    } else if (error?.type === 'popup_closed' || error?.error === 'popup_closed_by_user') {
      showAlert("Login cancelled.", "info");
      setItemLoading(false);
    } else {
      showAlert("Google authentication failed. Please try again.", "error");
      setItemLoading(false);
    }
  };

  const silentLoginConfig = {
    onSuccess: handleTokenSuccess,
    onError: handleSilentLoginError,
    onNonOAuthError: handleSilentNonOAuthError,
    scope: DRIVE_SCOPE,
    flow: "implicit",
    prompt: 'none',
    ...(storedEmail && { login_hint: storedEmail }),
  };

  const interactiveLoginConfig = {
    onSuccess: handleTokenSuccess,
    onError: handleInteractiveLoginError,
    onNonOAuthError: handleInteractiveNonOAuthError,
    scope: DRIVE_SCOPE,
    flow: "implicit",
    ...(storedEmail && { login_hint: storedEmail }),
  };

  const googleLoginSilent = useGoogleLogin(silentLoginConfig);
  const googleLoginInteractive = useGoogleLogin(interactiveLoginConfig);

  loginRef.current = googleLoginSilent;
  interactiveLoginRef.current = googleLoginInteractive;

  const handlePopupBlockedContinue = () => {
    setShowPopupBlockedModal(false);
    setItemLoading(true);
    loginActivityRef.current = false;
    silentLoginFailedRef.current = true;
    if (typeof interactiveLoginRef.current === "function") {
      interactiveLoginRef.current();
    }
  };

  const handleExportToSlides = async (includeNotes = false) => {
    let blobToExport = null;
    setItemLoading(true);
    if (generateBlob) {
      setIsExporting(true);
      try {
        blobToExport = await generateBlob(includeNotes);
        if (blobToExport) {
          setCurrentBlob(blobToExport);
        } else {
          showAlert("Failed to generate presentation.", "error");
          setIsExporting(false);
          return;
        }
      } catch (error) {
        console.error("Error generating blob:", error);
        showAlert("Failed to generate presentation. Please try again.", "error");
        setIsExporting(false);
        setItemLoading(false);
        return;
      }
    } else if (currentBlob) {
      blobToExport = currentBlob;
    }
    
    if (!blobToExport) {
      showAlert("No presentation to export. Please download the presentation first.", "warning");
      setIsExporting(false);
      setItemLoading(false);
      return;
    }

    loginActivityRef.current = false;
    silentLoginFailedRef.current = false;

    googleLoginSilent();

    popupTimeoutRef.current = setTimeout(() => {
      if (!loginActivityRef.current) {
        resetLoader();
        showAlert("Login cancelled or popup closed.", "info");
        setItemLoading(false);
      }
    }, 8000);

    timeoutRef.current = setTimeout(() => {
      resetLoader();
    }, LOGIN_TIMEOUT);
  };

  useImperativeHandle(ref, () => ({
    triggerExport: (includeNotes) => {
      handleExportToSlides(includeNotes);
    }
  }));

  return (<>
  <SpinLoader dataloader={item_loading} />
    <StyledExportButton
      onClick={() => handleExportToSlides(false)}
      disabled={isExporting}
      title="Export to Google Slides"
    >
      {isExporting ? <CircularProgress size={20} style={{ color: "white" }} /> : <SlidesIcon />}
    </StyledExportButton>
    <StyledModal
      open={showPopupBlockedModal}
      onClose={() => setShowPopupBlockedModal(false)}
      aria-labelledby="popup-blocked-modal-title"
      aria-describedby="popup-blocked-modal-description"
      slotProps={{
        backdrop: {
          style: { zIndex: 100000001 }
        }
      }}
    >
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white',
        padding: '24px',
        borderRadius: '8px',
        maxWidth: '400px',
        width: '90%',
        outline: 'none',
        zIndex: 100000002
      }}>
        <h2 id="popup-blocked-modal-title" style={{ marginTop: 0, marginBottom: '16px' }}>
          Popup Blocked
        </h2>
        <p id="popup-blocked-modal-description" style={{ marginBottom: '24px', color: '#666' }}>
          Popup is blocked by your browser. Please click here to continue login.
        </p>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <GoogleClassroomButton 
            style={{ width: '100%' }}
            onClick={handlePopupBlockedContinue}
          >
            Continue Login
          </GoogleClassroomButton>
        </div>
      </div>
    </StyledModal>
 </> );
});

export default ExportToSlides;
